import React, { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { useParams, useHistory } from "react-router-dom";
import { Spinner, Row, Col, Button } from "reactstrap";
import { getAllProperties, getPropertyDetailsById } from "services/services";
import "../../assets/scss/style.css";

const PropertyDetailPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        console.log("🔍 Looking for property ID:", id);
        
        // First, try to get full property details with developer, images, and documents
        try {
          console.log("Trying to fetch property details by ID...");
          const detailsRes = await getPropertyDetailsById(id);
          console.log("Property details response:", detailsRes);
          
          if (detailsRes?.success && detailsRes?.data) {
            // Set the full property details
            setPropertyDetails(detailsRes.data);
            
            // Set property from the response
            if (detailsRes.data.property) {
              setProperty(detailsRes.data.property);
              setLoading(false);
              return;
            }
          }
        } catch (detailsErr) {
          console.error("❌ Failed to fetch property details:", detailsErr);
          // Fall through to the backup method
        }
        
        let found = null;
        
        // Strategy 1: Try using property ID as search filter (should return just that property)
        try {
          console.log("Strategy 1: Trying search filter with property ID...");
          const searchRes = await getAllProperties(
            1,
            50,
            id, // Use the property ID as search filter
            "", // No from date
            "", // No to date
            "", // No status filter
            "", // No kyc status filter
            "false"
          );
          
          console.log("Search response:", searchRes);
          const searchItems = searchRes?.data?.items || searchRes?.data?.properties || searchRes?.data || [];
          console.log(`Found ${searchItems.length} properties via search`);
          
          found = searchItems.find((item) => {
            const matches = (item._id === id || item.id === id);
            if (matches) console.log("✅ Property match found:", item._id || item.id);
            return matches;
          });
          
          if (found) {
            console.log("✅ Property found via search filter");
            setProperty(found);
            return;
          } else {
            console.log("⚠️ Property not found in search results");
          }
        } catch (searchErr) {
          console.error("❌ Search filter failed:", searchErr);
        }
        
        // Strategy 2: Fetch multiple pages systematically
        console.log("Strategy 2: Trying systematic pagination...");
        const maxPages = 20; // Check up to 20 pages
        const pageSize = 100;
        
        for (let page = 1; page <= maxPages; page++) {
          try {
            console.log(`Fetching page ${page}...`);
            const res = await getAllProperties(
              page,
              pageSize,
              "", // No search filter
              "", // No from date
              "", // No to date
              "", // No status filter
              "", // No kyc status filter
              "false"
            );
            
            const items = res?.data?.items || res?.data?.properties || res?.data || [];
            console.log(`Page ${page}: ${items.length} properties`);
            
            found = items.find((item) => item._id === id || item.id === id);
            
            if (found) {
              console.log(`✅ Property found on page ${page}`);
              setProperty(found);
              return;
            }
            
            // If we got fewer items than page size, we've reached the end
            if (items.length < pageSize) {
              console.log(`Reached end of data at page ${page}`);
              break;
            }
          } catch (pageErr) {
            console.error(`Error fetching page ${page}:`, pageErr);
            break;
          }
        }
        
        console.error("❌ Property not found with ID:", id);
        setProperty(null);
        
      } catch (err) {
        console.error("❌ Failed to fetch property:", err);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center items-center p-10">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-10 text-center text-red-500 font-semibold">
        <h3>❌ Property not found</h3>
        <p style={{ marginTop: 10, fontSize: 16, color: "#666" }}>
          Property with ID "{id}" could not be found.
        </p>
        <Button
          className='btn all-common-btn'
          style={{ marginTop: 20 }}
          onClick={() => history.push("/admin/property")}
        >
          ← Back to Properties
        </Button>
      </div>
    );
  }

  return (
    // Use paddingTop to avoid overlapping header (adjust the px as needed)
    <div style={{ minHeight: "100vh", background: "#fafaf8", padding: "90px 0 32px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <Row className="mb-3">
  <Col className="d-flex justify-content-end">
    <Button
     className='btn all-common-btn'
      outline
      onClick={() => history.push("/admin/property")}
    >
      ← Back to Properties
    </Button>
  </Col>
</Row>

        <h2
          style={{
            fontWeight: 700,
            fontSize: "2rem",
            marginBottom: "28px",
            color: "#214",
            textAlign: "left"
          }}
        >
          Property Details
        </h2>
        {/* Main property details section */}
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Main Image */}
          <div style={{ flex: "0 0 560px" }}>
            <div
              style={{
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 4px 24px rgba(48,60,71,.12)",
                background: "#fff"
              }}
            >
              <img
                src={property?.photos?.[0] || "https://via.placeholder.com/600"}
                alt={property.title || "Property"}
                style={{
                  width: "100%",
                  height: 350,
                  objectFit: "cover",
                  display: "block"
                }}
              />
            </div>
          </div>

          {/* Listing Price and Main Info */}
          <div style={{ flex: 1, minWidth: 270 }}>
            <h1 style={{ fontSize: "2.5rem", fontWeight: 700, margin: 0, marginBottom: 10 }}>
              {property.listing_price
                ? `$${Number(property.listing_price).toLocaleString()}`
                : "Price on request"}
            </h1>
            {/* Commission badge */}
            <div
              style={{
                background: "#19af5b",
                color: "#fff",
                borderRadius: 32,
                padding: "10px 20px",
                fontWeight: 600,
                display: "inline-block",
                marginBottom: 18,
                fontSize: 19,
                marginTop: 0
              }}
            >
              {property.buyer_agent_commission
                ? `${property.buyer_agent_commission}% Buyer Agent Commission`
                : "2% Buyer Agent Commission"}
              <span style={{ marginLeft: 8, fontSize: 19, verticalAlign: "middle" }}>ℹ️</span>
            </div>
            {/* Address */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                color: "#2d816b",
                margin: "20px 0",
                fontWeight: 500,
                fontSize: 18
              }}
            >
              <MapPin style={{ marginRight: 8, color: "#19af5b" }} />
              {property.address || "Address not available"}
            </div>
            {/* Map */}
            <div style={{ margin: "18px 0 0 0", borderRadius: 13, overflow: "hidden", width: "fit-content" }}>
              <iframe
                title="map"
                width={240}
                height={120}
                style={{ border: 0, borderRadius: 13 }}
                loading="lazy"
                allowFullScreen
                src={`https://maps.google.com/maps?q=${encodeURIComponent(property.address || "")}&output=embed`}
              />
            </div>
          </div>
        </div>

        {/* Facts and Features */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "34px auto 0 auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 17, letterSpacing: ".2px" }}>
            Facts & Features
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>House</div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.property_square_footage || 0} /sqft
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.year_built || "Year not listed"}
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.lot_size || "0-acre lot"}
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Property Details
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.total_rooms || 0} Total Rooms
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.total_bathrooms || 0} Bathrooms
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.total_bedrooms || 0} Bedrooms
              </div>
            </div>
          </div>
        </div>

      

        {/* Amenities */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Amenities
          </div>
          <div style={{ color: "#566", fontSize: 18, minHeight: 30 }}>
            {(() => {
              // Combine all amenity fields
              const allAmenities = [
                ...(property.amenities || []),
                ...(property.additional_amenities || []),
                ...(property.building_amenities || []),
                ...(property.outdoor_amenities || []),
                ...(property.other_amenities || [])
              ].filter(Boolean);
              
              console.log("All amenities:", allAmenities);
              console.log("Property amenities data:", {
                amenities: property.amenities,
                additional_amenities: property.additional_amenities,
                building_amenities: property.building_amenities,
                outdoor_amenities: property.outdoor_amenities,
                other_amenities: property.other_amenities
              });
              
              if (allAmenities.length > 0) {
                return (
                  <ul style={{ paddingLeft: 16, color: "#355" }}>
                    {allAmenities.map((amenity, i) => (
                      <li key={i} style={{ marginBottom: 8 }}>{amenity}</li>
                    ))}
                  </ul>
                );
              } else {
                return (
                  <span style={{ color: "#859", fontStyle: "italic" }}>
                    This seller has not listed any amenities for this listing. Please contact the seller to know more.
                  </span>
                );
              }
            })()}
          </div>
        </div>

        {/* Description */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Description
          </div>
          <div style={{ color: "#566", fontSize: 18, minHeight: 40 }}>
            {property.description || (
              <span style={{ color: "#859", fontStyle: "italic" }}>
                No description available.
              </span>
            )}
          </div>
        </div>

        {/* Listing Information */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Listing Information
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {property.agentId?.name && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 250,
                  flex: "1 1 240px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Agent Name</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {property.agentId.name}
                </div>
              </div>
            )}
            {property.agentId?.email && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 280,
                  flex: "1 1 260px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Agent Email</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {property.agentId.email}
                </div>
              </div>
            )}
            {property.realStateOffice?.companyName && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 250,
                  flex: "1 1 240px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Real Estate Office</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {property.realStateOffice.companyName}
                </div>
              </div>
            )}
            {property.listingApprovalEmail && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 280,
                  flex: "1 1 260px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Listing Approval Email</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {property.listingApprovalEmail}
                </div>
              </div>
            )}
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Status</div>
              <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                {property.status ? property.status.charAt(0).toUpperCase() + property.status.slice(1) : "Pending"}
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Approved</div>
              <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                {property.is_approved ? "Yes" : "No"}
              </div>
            </div>
            {property.approvedAt && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 180,
                  flex: "1 1 160px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Approved At</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {new Date(property.approvedAt).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Token Usage */}
        {property.aiPropertyDetailsTokenUsage && (
          <div
            style={{
              background: "#fff",
              borderRadius: 15,
              margin: "30px auto",
              padding: 24,
              boxShadow: "0 2px 18px rgba(60,60,110,0.07)",
              border: "1px solid #c8e6c9"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <span
                style={{
                  background: "#e8f5e9",
                  color: "#2e7d32",
                  border: "1px solid #c8e6c9",
                  borderRadius: 20,
                  padding: "4px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5
                }}
              >
                <i className="bx bx-bot" style={{ fontSize: 15 }}></i>
                AI Generated
              </span>
              <div style={{ fontWeight: 700, fontSize: 22, color: "#214" }}>AI Token Usage</div>
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {/* Total Tokens — highlighted */}
              <div
                style={{
                  background: "#e8f5e9",
                  border: "1px solid #a5d6a7",
                  padding: "16px 24px",
                  borderRadius: 12,
                  minWidth: 170,
                  flex: "1 1 160px",
                  textAlign: "center"
                }}
              >
                <div style={{ color: "#555", fontSize: 13, marginBottom: 6 }}>Total Tokens</div>
                <div style={{ color: "#1b5e20", fontWeight: 700, fontSize: 26 }}>
                  {property.aiPropertyDetailsTokenUsage.totalTokens?.toLocaleString() ?? "-"}
                </div>
              </div>
              {/* Input Tokens */}
              <div
                style={{
                  background: "#f5faf6",
                  padding: "16px 24px",
                  borderRadius: 12,
                  minWidth: 160,
                  flex: "1 1 150px",
                  textAlign: "center"
                }}
              >
                <div style={{ color: "#555", fontSize: 13, marginBottom: 6 }}>Total Input Tokens</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 20 }}>
                  {property.aiPropertyDetailsTokenUsage.totalInputTokens?.toLocaleString() ?? "-"}
                </div>
              </div>
              {/* Output Tokens */}
              <div
                style={{
                  background: "#f5faf6",
                  padding: "16px 24px",
                  borderRadius: 12,
                  minWidth: 160,
                  flex: "1 1 150px",
                  textAlign: "center"
                }}
              >
                <div style={{ color: "#555", fontSize: 13, marginBottom: 6 }}>Total Output Tokens</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 20 }}>
                  {property.aiPropertyDetailsTokenUsage.totalOutputTokens?.toLocaleString() ?? "-"}
                </div>
              </div>
              {/* Last Saved Total */}
              <div
                style={{
                  background: "#f5faf6",
                  padding: "16px 24px",
                  borderRadius: 12,
                  minWidth: 160,
                  flex: "1 1 150px",
                  textAlign: "center"
                }}
              >
                <div style={{ color: "#555", fontSize: 13, marginBottom: 6 }}>Last Saved Tokens</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 20 }}>
                  {property.aiPropertyDetailsTokenUsage.lastSavedTotalTokens?.toLocaleString() ?? "-"}
                </div>
              </div>
              {/* Last Saved Input */}
              <div
                style={{
                  background: "#f5faf6",
                  padding: "16px 24px",
                  borderRadius: 12,
                  minWidth: 160,
                  flex: "1 1 150px",
                  textAlign: "center"
                }}
              >
                <div style={{ color: "#555", fontSize: 13, marginBottom: 6 }}>Last Saved Input</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 20 }}>
                  {property.aiPropertyDetailsTokenUsage.lastSavedInputTokens?.toLocaleString() ?? "-"}
                </div>
              </div>
              {/* Last Saved Output */}
              <div
                style={{
                  background: "#f5faf6",
                  padding: "16px 24px",
                  borderRadius: 12,
                  minWidth: 160,
                  flex: "1 1 150px",
                  textAlign: "center"
                }}
              >
                <div style={{ color: "#555", fontSize: 13, marginBottom: 6 }}>Last Saved Output</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 20 }}>
                  {property.aiPropertyDetailsTokenUsage.lastSavedOutputTokens?.toLocaleString() ?? "-"}
                </div>
              </div>
            </div>
            {property.aiPropertyDetailsTokenUsage.lastSavedAt && (
              <div style={{ marginTop: 14, color: "#888", fontSize: 13 }}>
                Last saved at:{" "}
                <span style={{ fontWeight: 600, color: "#555" }}>
                  {new Date(property.aiPropertyDetailsTokenUsage.lastSavedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Footer-style stats */}
        <div
          style={{
            display: "flex",
            gap: 40,
            margin: "40px 0 8px 0",
            fontSize: 17,
            color: "#666",
            justifyContent: "start"
          }}
        >
          <div>
            <span style={{ fontWeight: 600 }}>0 days</span> on Quotivo
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>{property.views || 0}</span> Views
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>{property.saves || 0}</span> Saves
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
